import React, { useCallback, useState } from "react";

import { Tooltip } from "@braze/beacon-core";
import { useTooltipOnTruncate } from "@braze/beacon-lab-hooks";

import { StackOrder } from "@lib/canvasStackOrder";
import { strictExhaustiveCheck } from "@lib/exhaustiveCheck";

import useI18nNamespace from "@services/useI18nNamespace";

import {
  DiffColorContent,
  DiffColorHex,
  DiffColorSwatch,
  DiffDisplayContainer,
  DiffDisplayTitle,
  DiffEditorLabel,
  DiffImage,
  DiffImageContent,
  DiffTextContent,
  DiffTextPlaceholder,
} from "./DiffDisplay.style";
import type { DiffDisplayProps } from "./types";

const ColorValue = ({ color }: { color: string }) => (
  <DiffColorContent>
    <DiffColorSwatch color={color} />
    <DiffColorHex>{color}</DiffColorHex>
  </DiffColorContent>
);

const ImageValue = ({ url }: { url: string }) => {
  const { t } = useI18nNamespace("agentic-diff-display");
  const [hasError, setHasError] = useState(false);
  const alt = t("image-preview", "Image preview");

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  if (hasError) {
    return (
      <DiffTextContent>
        <DiffTextPlaceholder>{t("image-load-error", "Unable to preview image")}</DiffTextPlaceholder>
      </DiffTextContent>
    );
  }

  return (
    <DiffImageContent>
      <DiffImage src={url} alt={alt} onError={handleError} />
    </DiffImageContent>
  );
};

/**
 * A simple diff display component that shows original and modified text
 * side by side without syntax highlighting.
 * When type is "color", renders color swatches with hex codes instead of text.
 * When type is "image", renders an image preview from the provided URL.
 */
export const DiffDisplay = ({ title, original, modified, type = "text", placeholder }: DiffDisplayProps) => {
  const { t } = useI18nNamespace("agentic-diff-display");
  const zIndex = StackOrder.OVERLAY_CONTENT_ABOVE_POPOVER;
  const [tooltipProps, targetRef] = useTooltipOnTruncate<HTMLSpanElement>();

  const defaultPlaceholder = placeholder ?? t("none-placeholder", "None");

  const renderValue = (value: string) => {
    if (!value) {
      return (
        <DiffTextContent>
          <DiffTextPlaceholder>{defaultPlaceholder}</DiffTextPlaceholder>
        </DiffTextContent>
      );
    }
    switch (type) {
      case "color":
        return <ColorValue color={value} />;
      case "image":
        // key={value} forces remount when the URL changes so onError state doesn't leak across URLs
        return <ImageValue key={value} url={value} />;
      case "text":
        return <DiffTextContent>{value}</DiffTextContent>;
      default:
        return strictExhaustiveCheck(type);
    }
  };

  if (original) {
    return (
      <DiffDisplayContainer>
        <DiffDisplayTitle ref={targetRef}>{title}</DiffDisplayTitle>
        <Tooltip {...tooltipProps} targetRef={targetRef} zIndex={zIndex}>
          {title}
        </Tooltip>
        <div>
          <DiffEditorLabel>{t("previous", "Previous")}</DiffEditorLabel>
          {renderValue(original)}
        </div>
        <div>
          <DiffEditorLabel>{t("proposed", "Proposed")}</DiffEditorLabel>
          {renderValue(modified)}
        </div>
      </DiffDisplayContainer>
    );
  }

  return (
    <DiffDisplayContainer>
      <DiffDisplayTitle ref={targetRef}>{title}</DiffDisplayTitle>
      <Tooltip {...tooltipProps} targetRef={targetRef} zIndex={zIndex}>
        {title}
      </Tooltip>
      {renderValue(modified)}
    </DiffDisplayContainer>
  );
};
